import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * What the fields inside a `form` collect, in one place.
 *
 * An `input` on its own is a box a visitor types into and nothing keeps what they typed. The same node
 * inside a `form` is one answer of that form's submission, and nothing about the node changes — only
 * the scope it is drawn in. `form` puts one set of answers in scope; every field below it reads and
 * writes that one set, registers the rules its merchant gave it, and no answer leaks past the form
 * around it. Written the way `commerce/repeaterScope.tsx` puts a row in scope, so the codebase has one
 * idiom for a subtree that is drawn under something.
 *
 * A field drawn outside every form still works: it holds an island of its own, exactly as a checkout
 * element does outside `CheckoutScope`, and what it collects reaches nothing — which is the truth.
 */

/** What one control holds: text, a tick, or the addresses a file picker uploaded. */
export type FieldValue = string | boolean | readonly string[];

/** The shapes a merchant may ask an answer to take; the only shapes a field is ever checked against. */
export type FieldShape = 'none' | 'email' | 'phone' | 'number';

/** One field's rules exactly as its merchant set them, plus the DOM id a `label` needs to reach it. */
export type FieldRule = {
  name: string;
  label: string;
  /** The words shown in an empty field, which name it in a message when it has no caption. */
  placeholder?: string;
  domId: string;
  required: boolean;
  /** Null wherever the merchant set no such rule, so nothing is checked that nobody asked for. */
  minLength: number | null;
  maxLength: number | null;
  shape: FieldShape;
};

/**
 * How the last submission went, which is the whole of what a form tells a visitor about itself.
 * `invalid` is answers refused before sending, each field saying why under itself; `failed` is a
 * submission that could not be stored, the one case the form's own banner speaks for.
 */
export type FormStatus = 'idle' | 'sending' | 'sent' | 'invalid' | 'failed';

/** One form's answers, its fields' rules, and the four calls that change them. */
export type FormAnswers = {
  values: Readonly<Record<string, FieldValue>>;
  errors: Readonly<Record<string, string>>;
  status: FormStatus;
  /** Every rule a field below this form registered, in the order the fields mounted. */
  rules: readonly FieldRule[];
  setValue: (name: string, value: FieldValue) => void;
  setErrors: (errors: Readonly<Record<string, string>>) => void;
  setStatus: (status: FormStatus) => void;
  /** Joins one field to the form and answers the call that takes it out again when it unmounts. */
  register: (rule: FieldRule) => () => void;
};

const FormContext = createContext<FormAnswers | null>(null);

/** Nothing has been refused yet; frozen and shared so a form at rest allocates no error bag of its own. */
const NO_ERRORS: Readonly<Record<string, string>> = Object.freeze(Object.create(null));

// Writes one key into a prototype-less bag: field names come from the merchant, so `constructor` is absent too.
const withKey = <T,>(bag: Readonly<Record<string, T>>, key: string, value: T): Record<string, T> =>
  Object.assign(Object.create(null), bag, { [key]: value });

/** Holds one form's state. The `form` element owns one; a field outside every form holds its own island. */
export function useFormAnswers(): FormAnswers {
  const [values, setValues] = useState<Record<string, FieldValue>>(() => Object.create(null));
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>(NO_ERRORS);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [rules, setRules] = useState<readonly FieldRule[]>([]);

  const setValue = useCallback((name: string, value: FieldValue) => {
    setValues((current) => withKey(current, name, value));
    setErrors((current) => (current[name] ? withKey(current, name, '') : current));
  }, []);

  const register = useCallback((rule: FieldRule) => {
    setRules((current) => [...current.filter((held) => held.domId !== rule.domId), rule]);
    return () => setRules((current) => current.filter((held) => held.domId !== rule.domId));
  }, []);

  return useMemo(
    () => ({ values, errors, status, rules, setValue, setErrors, setStatus, register }),
    [values, errors, status, rules, setValue, register]
  );
}

/** Joins every field drawn inside it to one set of answers, which the `form` around them submits. */
export const FormScope: React.FC<{ answers: FormAnswers; children: ReactNode }> = ({ answers, children }) => (
  <FormContext.Provider value={answers}>{children}</FormContext.Provider>
);

// Serves the form in scope, or nothing at all for a field a merchant dropped outside every form.
export const useEnclosingForm = (): FormAnswers | null => useContext(FormContext);

// Serves the form a field belongs to, or an island of its own so a stray field still types and still shows.
export function useFormOrIsland(): FormAnswers {
  const shared = useContext(FormContext);
  const island = useFormAnswers();
  return shared ?? island;
}

// Names one field in a sentence a visitor reads: its caption, else the words shown in it, else its own name.
export const fieldTitle = (rule: FieldRule): string => rule.label || rule.placeholder || rule.name;

// Names the DOM id one field name reaches, or null when no field in this form answers to that name.
export const fieldIdOf = (rules: readonly FieldRule[], name: string): string | null =>
  rules.find((rule) => rule.name === name)?.domId ?? null;
